// CreateShiftSettings.tsx
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  requiredArrayValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import {
  DAYS_OF_WEEK,
  SHIFT_NAMES,
  slotDurationOptions,
  STATUSARRAY,
} from "../../_contstants/common";
import {
  createShiftSetting,
  getAllShiftSettings,
  getShiftSettingByID,
  updateShiftSetting,
} from "./services/shiftSettings.service";
import { clearShiftSettingsDetails } from "./shiftSettingsSlice";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import FormTimePicker from "../../components/formFields/FormTimeField";
import { getAllActiveBranches } from "../branch/services/branch.services";
import { getAllActiveDoctors } from "../doctor/services/doctor.service";
import moment from "moment";

const CreateShiftSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const ShiftSettingsID = useAppSelector((state) => state.drawer.data);
  const ShiftSettingsDetail = useAppSelector(
    (state) => state.shiftSettings.ShiftSettingsDetail,
  );
  const loading = useAppSelector((state) => state.shiftSettings.formLoading);
  // const BranchList = useAppSelector((state) => state.branch.BranchList);
  const DoctorList = useAppSelector((state) => state.doctor.DoctorList);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ShiftSettingsDetail]);

  useEffect(() => {
    dispatch(getAllActiveDoctors());
    dispatch(getAllActiveBranches());

    if (ShiftSettingsID) {
      dispatch(getShiftSettingByID(ShiftSettingsID));
    }
    return () => {
      dispatch(clearShiftSettingsDetails());
    };
  }, [ShiftSettingsID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      DoctorID: values.DoctorID ?? 0,
      ShiftName: values.ShiftName ?? "",
      StartTime: values.StartTime
        ? moment(values.StartTime).format("hh:mm A")
        : "",
      EndTime: values.EndTime ? moment(values.EndTime).format("hh:mm A") : "",
      SlotDuration: values.SlotDuration ? parseInt(values.SlotDuration) : 15,
      // BranchID: values.BranchID ?? 0,
      ApplicableDays: Array.isArray(values.ApplicableDays)
        ? values.ApplicableDays.join(",")
        : values.ApplicableDays,
      IsActive: values.IsActive === 1,
    };

    try {
      if (ShiftSettingsID) {
        const editPayload = {
          ...formData,
          ID: ShiftSettingsID,
        };
        const response = await dispatch(updateShiftSetting(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllShiftSettings());
          dispatch(clearShiftSettingsDetails());
        }
      } else {
        const response = await dispatch(createShiftSetting(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllShiftSettings());
          dispatch(clearShiftSettingsDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={ShiftSettingsDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {ShiftSettingsID
                      ? "Update Shift Setting"
                      : "Add Shift Setting"}
                  </Typography.h4>
                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="ShiftName"
                    label="Shift Name "
                    placeholder="Select shift name"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={SHIFT_NAMES}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="DoctorID"
                    label="Doctor "
                    placeholder="Select doctor"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={DoctorList.map((doctor: any) => ({
                      value: doctor.ID,
                      label: doctor.DoctorName,
                    }))}
                  />
                </GridLayoutItem>

                {/* <GridLayoutItem>
                  <Field
                    name="BranchID"
                    label="Branch "
                    placeholder="Select branch"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={BranchList.map((branch: any) => ({
                      value: branch.ID,
                      label: branch.BranchName,
                    }))}
                  />
                </GridLayoutItem> */}

                <GridLayoutItem>
                  <Field
                    name="StartTime"
                    label="Start Time"
                    placeholder="e.g., 09:00 AM"
                    component={FormTimePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="EndTime"
                    label="End Time"
                    placeholder="e.g., 06:00 PM"
                    component={FormTimePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  {/* <Field
                    name="SlotDuration"
                    label="Slot Duration (minutes) "
                    placeholder="e.g., 15, 30, 45, 60"
                    component={FormTextField}
                    type="number"
                    validator={requiredValidator}
                    astrike={true}
                  /> */}

                  <Field
                    name="SlotDuration"
                    label="Slot Duration"
                    placeholder="Select slot duration"
                    component={FormSelectionField}
                    options={slotDurationOptions}
                    validator={requiredValidator}
                    astrike={true}
                    wrapperStyle={{ width: "100%" }}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="ApplicableDays"
                    label="Applicable Days "
                    placeholder="Select applicable days"
                    component={FormMultiSelectionFiled}
                    validator={requiredArrayValidator}
                    astrike={true}
                    options={DAYS_OF_WEEK}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => ({
                      value: status?.value,
                      label: status?.label,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={ShiftSettingsID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateShiftSettings;
